/** Constantes structurelles de la simulation. Aucune n'est ajustable à chaud :
 *  les changer invalide les replays existants. */

/* ── Cadence (PLAN.md §3.4) ────────────────────────────────────────────────
   120 Hz et non 60. Deux raisons :
   1. À 1 500 u/s (vitesse plafond d'un projectile) et 60 Hz, une balle avance
      de 25 u par pas contre 9 u de rayon de hitbox joueur — elle traverse sans
      toucher. À 120 Hz elle avance de 12,5 u, ce qui reste au-delà du rayon :
      d'où la collision BALAYÉE, obligatoire de toute façon (collisionSystem).
   2. La latence d'input tombe de moitié et le mouvement est plus net.
   Coût : 2 sous-pas par frame à 60 fps, soit ~1,6 ms.

   ⚠️ Le pas est FIXE et jamais adaptatif. Une machine lente rend moins de
   frames — elle ne simule pas moins. Sinon les replays divergent selon le
   matériel, ce qui est précisément le contraire du but. */
export const TICK_HZ = 120;
export const DT = 1 / TICK_HZ;

/** Au-delà, on abandonne le retard au lieu de l'accumuler : le temps de jeu
 *  RALENTIT sur machine faible plutôt que de sauter. Pour un shmup, où chaque
 *  frame sautée peut être une mort non vue, c'est le bon compromis. */
export const MAX_STEPS = 6;

/** Retour d'onglet en arrière-plan : sans ce plafond, l'accumulateur reçoit
 *  plusieurs secondes d'un coup et la boucle entre en spirale de la mort. */
export const MAX_FRAME_DELTA = 0.25;

/* ── Cadre spatial (PLAN.md §4.2) ─────────────────────────────────────────
   Tout le gameplay vit strictement à Z = 0 : joueur, ennemis, projectiles,
   pickups, hurtbox. Aucune exception. La 3D est dans le décor et la mise en
   scène, jamais dans le plan de jeu — sinon l'anticipation des trajectoires
   devient impossible. */
export const PF_W = 900;
export const PF_H = 1200;
export const PF_HW = PF_W / 2;
export const PF_HH = PF_H / 2;

/** Marge au-delà de laquelle une entité est éliminée. Assez large pour qu'un
 *  ennemi puisse entrer en scène depuis le hors-champ. */
export const CULL_MARGIN = 140;

/* ── Capacités des pools ──────────────────────────────────────────────────
   Ce sont des PLAFONDS DE SÉCURITÉ, pas des cibles. La densité de jeu visée
   est de 80 à 200 projectiles simultanés (PLAN.md §6.4) ; ces capacités
   existent pour que le banc de charge puisse pousser à 10× sans réallouer,
   et pour qu'un pattern buggé sature au lieu de planter. */
export const CAP_ENEMY_BULLETS = 2048;
export const CAP_PLAYER_BULLETS = 512;
export const CAP_ENEMIES = 256;
export const CAP_PICKUPS = 128;
/** Obstacles simultanés (astéroïdes, plus tard les complexes militaires). Le
 *  décor collisionnable est CLAIRSEMÉ par nature — quelques rochers à l'écran,
 *  jamais un rideau — d'où un plafond bien plus bas que les projectiles. C'est
 *  un filet de sécurité contre un émetteur d'obstacles buggé, pas une cible. */
export const CAP_OBSTACLES = 64;

/** Vitesse plafond d'un projectile ennemi. 1 500 u/s = 0,80 s pour traverser
 *  l'écran. Le temps de réaction visuel humain étant d'environ 250 ms, cela
 *  laisse une marge de 3×. Au-delà, le jeu devient de la mémorisation. */
export const MAX_BULLET_SPEED = 1500;

/** Au-dessus de cette vitesse, le test ponctuel ne suffit plus et la collision
 *  balayée s'impose (voir collisionSystem). */
export const SWEEP_THRESHOLD = 1000;

/** Vitesse de DÉFILEMENT du monde, en u/s. C'est le rythme auquel le décor
 *  descend vers le joueur figé en bas.
 *
 *  ⚠️ Un obstacle n'est PAS un ennemi : il ne pilote pas sa propre trajectoire,
 *  il suit le défilement global (voir `obstacleSystem`), là où un ennemi descend
 *  à sa `motion.speed` propre. Cette valeur est donc la constante de moteur qui
 *  fixe ce défilement, et elle vit ici, dans la simulation, parce qu'elle
 *  gouverne des positions collisionnables — donc des ticks à venir.
 *
 *  Elle est calée sur les 190 u/s que la vue applique déjà au décor et aux
 *  débris d'explosion (`Backdrop.SCROLL_SPEED`, et le `190 * dt` du point
 *  d'entrée) : un obstacle qui glisserait à une autre vitesse dériverait
 *  visiblement par rapport à la poussière et à la tranchée, et trahirait qu'il
 *  n'appartient pas au même monde. Les deux nombres décrivent la même chose vue
 *  de deux côtés de la frontière sim/vue — mais celui-ci est le seul qui
 *  influence le gameplay, donc le seul qui entre dans l'empreinte de replay. */
export const OBSTACLE_SCROLL_SPEED = 190;

/* ── ULTIME (super-attaque chargée) — PLAN.md §6 ──────────────────────────────
   Une JAUGE de combat se remplit aux kills et aux frôlements ; pleine, la touche
   d'ulti déclenche une brève CHARGE (télégraphe visuel + audio), puis une
   DÉCHARGE : une volée massivement scalée de la secondaire courante, tirée en
   rafale. Toute cette machinerie est déterministe et entre dans l'empreinte de
   replay (voir Snapshot). */
/** Remplissage de la jauge par ennemi tué (≈ 17 kills pour une jauge pleine). */
export const ULTI_FILL_KILL = 0.06;
/** Remplissage par frôlement — récompense l'agressivité, comme le score. */
export const ULTI_FILL_GRAZE = 0.006;
/** Durée de la CHARGE avant la décharge, en secondes (le télégraphe). */
export const ULTI_CHARGE_SEC = 0.9;
/** Invulnérabilité accordée pendant la charge : l'ulti est une fantaisie de
 *  puissance, pas un suicide — on ne meurt pas en la déclenchant. */
export const ULTI_CHARGE_IFRAMES = ULTI_CHARGE_SEC + 0.25;
/** Nombre de VOLÉES de la décharge, et leur espacement — une rafale monstrueuse. */
export const ULTI_SHOTS = 5;
export const ULTI_SHOT_EVERY = 0.07;
/** Facteurs de scaling de la volée d'ulti par rapport à la secondaire de base. */
export const ULTI_COUNT_MUL = 6;
export const ULTI_DMG_MUL = 6;
export const ULTI_RADIUS_MUL = 2.1;

/* ── CHAUFFE DU CANON (tir manuel) — retour de jeu ────────────────────────────
   Le tir primaire n'est plus AUTOMATIQUE : on appuie (clic gauche) pour tirer,
   et le canon CHAUFFE à chaque coup, refroidit au repos. En SURCHAUFFE, la
   cadence s'effondre et le canon rougit — une gestion de ressource qui récompense
   les rafales maîtrisées plutôt que la gâchette bloquée. État déterministe, dans
   l'empreinte de replay (voir Snapshot). */
/** Chaleur ajoutée par coup tiré (0 = froid, 1 = surchauffe). */
export const HEAT_PER_SHOT = 0.08;
/** Refroidissement par seconde, appliqué en permanence (le tir le compense). */
export const HEAT_COOL_RATE = 0.5;
/** Seuil d'entrée en surchauffe. */
export const HEAT_OVERHEAT = 1;
/** Plafond de chaleur — STRICTEMENT au-dessus du seuil de surchauffe, et c'est
 *  crucial : le refroidissement s'applique en tête de tick, AVANT le test de
 *  surchauffe. Si le plafond valait le seuil, un canon martelé oscillerait à
 *  0,996 et n'entrerait JAMAIS en surchauffe. La marge au-dessus garantit qu'un
 *  tir soutenu franchit bel et bien la ligne. C'est aussi la ZONE ROUGE VIF
 *  ([1, HEAT_MAX]) que la vue exploite pour l'incandescence. */
export const HEAT_MAX = 1.3;
/** Seuil de SORTIE de surchauffe (hystérésis) : il faut vraiment refroidir avant
 *  de retrouver sa pleine cadence — sinon on clignoterait à la frontière. */
export const HEAT_RECOVER = 0.35;
/** Multiplicateur de recharge en surchauffe : la cadence est divisée d'autant. */
export const OVERHEAT_FIRE_MUL = 3;
/** Durée, en secondes, pendant laquelle on peut TIRER en surchauffe (rouge vif)
 *  avant que le canon ne s'ENRAYE. Ignorer l'alarme et marteler la gâchette a un
 *  prix dur : le tir se bloque NET (fumée) jusqu'au refroidissement complet
 *  (`HEAT_RECOVER`). Relâcher en surchauffe remet ce compteur à zéro — la
 *  discipline évite l'enrayage, l'entêtement le provoque. */
export const OVERHEAT_JAM_SEC = 1.2;

/* ── POWER-UPS qui TOMBENT (bonus INSTANTANÉS) — retour de jeu ─────────────────
   En plus des cartes de niveau, des ennemis lâchent parfois un JETON qui descend ;
   l'attraper déclenche un bonus immédiat. C'est un second axe de progression, plus
   nerveux (il faut aller le chercher, sous le feu), qui coexiste avec les cartes.
   La chute/aimantation/ramassage réutilise le système de ramassables ; seul
   l'EFFET est neuf. Tout est déterministe (tirage `w.rng`, état haché). */
/** Probabilité de BASE qu'un ennemi lâche un power-up à sa mort, multipliée par
 *  `(1 + rang)` : les gros en lâchent plus souvent (0 → 4 %, 2 → 12 %). Volontairement
 *  bas — un power-up est un événement, pas une pluie constante. */
export const POWERUP_DROP_BASE = 0.04;
/** Vitesse verticale (vers le bas) imprimée au jeton à l'éjection, en u/s. */
export const POWERUP_DROP_VY = 110;
/** Fraction de jauge d'ulti rendue par un jeton SURGE. */
export const POWERUP_SURGE_FILL = 0.5;
/** Vies au-delà desquelles un jeton RÉPARATION est gaspillé, faute de contrainte
 *  imposant un plafond plus bas (`livesCap`). */
export const POWERUP_LIFE_MAX = 5;
/** Plafond de bombes en stock via les jetons BOMBE. */
export const POWERUP_BOMB_MAX = 6;
/** Durée de la SURALIMENTATION (Overdrive) : cadence ×2 et canon glacé. */
export const POWERUP_OVERDRIVE_SEC = 5;
/** Multiplicateur de recharge en suralimentation (< 1 = plus rapide). */
export const OVERDRIVE_FIRE_MUL = 0.5;
